import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Loader2,
  Mail,
  Send,
  Eye,
  ShieldCheck,
  Users,
  CalendarDays,
  RefreshCw,
  CheckCircle,
  XCircle,
  Search,
  ListChecks,
  UserCog
} from 'lucide-react';
import { useApiClient } from '../hooks/useApiClient';
import { useToast } from '../hooks/useToast';
import { Toast, ConfirmDialog } from '../components/Toast';
import { RecipientPicker } from '../components/RecipientPicker';
import { envConfig } from '../config/authConfig';

const API_BASE_URL = envConfig.apiBaseUrl;

type DigestTab = 'team' | 'manager';
type ManagerMode = 'one' | 'bulk';

interface ManagerOption {
  employeeId: string;
  name: string;
  email: string;
  directReportCount: number;
}

interface BulkSendResult {
  total: number;
  succeeded: number;
  failed: number;
  results: { email: string; success: boolean; error?: string }[];
}

export function AdminDigestPage() {
  const navigate = useNavigate();
  const { apiCall } = useApiClient();
  const { toasts, hideToast, success: showSuccess, error: showError } = useToast();

  // Admin check
  const [authChecking, setAuthChecking] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  // Top-level tab
  const [activeTab, setActiveTab] = useState<DigestTab>('manager');

  // ─── Team Digest state ─────────────────────────────────────────────────────
  const [teamPreviewHtml, setTeamPreviewHtml] = useState<string>('');
  const [teamPreviewLoading, setTeamPreviewLoading] = useState(false);
  const [teamRecipients, setTeamRecipients] = useState<string[]>([]);
  const [teamSubject, setTeamSubject] = useState<string>('');
  const [teamSending, setTeamSending] = useState(false);
  const [teamConfirmOpen, setTeamConfirmOpen] = useState(false);

  // ─── Manager Digest state ──────────────────────────────────────────────────
  const [managerMode, setManagerMode] = useState<ManagerMode>('one');
  const [managers, setManagers] = useState<ManagerOption[]>([]);
  const [managersLoading, setManagersLoading] = useState(false);
  const [managerSearch, setManagerSearch] = useState('');

  // "One manager" sub-tab
  const [selectedManagerId, setSelectedManagerId] = useState<string>('');
  const [managerPreviewHtml, setManagerPreviewHtml] = useState<string>('');
  const [managerPreviewLoading, setManagerPreviewLoading] = useState(false);
  const [managerCc, setManagerCc] = useState<string[]>([]);
  const [managerSubject, setManagerSubject] = useState<string>('');
  const [managerSending, setManagerSending] = useState(false);
  const [managerConfirmOpen, setManagerConfirmOpen] = useState(false);

  // "Bulk" sub-tab
  const [bulkSelectedIds, setBulkSelectedIds] = useState<Set<string>>(new Set());
  const [bulkCc, setBulkCc] = useState<string[]>([]);
  const [bulkSubject, setBulkSubject] = useState<string>('');
  const [bulkSending, setBulkSending] = useState(false);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkResult, setBulkResult] = useState<BulkSendResult | null>(null);

  // ─── Admin gate ────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        const resp = await apiCall(`${API_BASE_URL}/api/Admin/me`);
        if (!cancelled) {
          if (resp.ok) {
            const json = await resp.json();
            setIsAdmin(Boolean(json?.data?.isAdmin));
          } else {
            setIsAdmin(false);
          }
        }
      } catch {
        if (!cancelled) setIsAdmin(false);
      } finally {
        if (!cancelled) setAuthChecking(false);
      }
    };
    check();
    return () => {
      cancelled = true;
    };
  }, [apiCall]);

  // ─── Team digest: load preview on demand ───────────────────────────────────
  const loadTeamPreview = useCallback(async () => {
    setTeamPreviewLoading(true);
    try {
      const resp = await apiCall(`${API_BASE_URL}/api/Admin/team-digest/preview`);
      if (resp.ok) {
        const html = await resp.text();
        setTeamPreviewHtml(html);
      } else {
        showError('Failed to load team digest preview');
      }
    } catch {
      showError('Failed to load team digest preview');
    } finally {
      setTeamPreviewLoading(false);
    }
  }, [apiCall, showError]);

  // ─── Manager digest: load list & preview ───────────────────────────────────
  const loadManagers = useCallback(async () => {
    setManagersLoading(true);
    try {
      const resp = await apiCall(`${API_BASE_URL}/api/Admin/managers`);
      if (resp.ok) {
        const json = await resp.json();
        setManagers(Array.isArray(json.data) ? json.data : []);
      } else {
        showError('Failed to load managers list');
      }
    } catch {
      showError('Failed to load managers list');
    } finally {
      setManagersLoading(false);
    }
  }, [apiCall, showError]);

  const loadManagerPreview = useCallback(
    async (mgrId: string) => {
      if (!mgrId) return;
      setManagerPreviewLoading(true);
      try {
        const resp = await apiCall(
          `${API_BASE_URL}/api/Admin/manager-digest/preview?managerId=${encodeURIComponent(mgrId)}`
        );
        if (resp.ok) {
          const html = await resp.text();
          setManagerPreviewHtml(html);
        } else {
          showError('Failed to load manager digest preview');
        }
      } catch {
        showError('Failed to load manager digest preview');
      } finally {
        setManagerPreviewLoading(false);
      }
    },
    [apiCall, showError]
  );

  // Load managers when entering the manager tab the first time
  useEffect(() => {
    if (isAdmin && activeTab === 'manager' && managers.length === 0 && !managersLoading) {
      loadManagers();
    }
  }, [isAdmin, activeTab, managers.length, managersLoading, loadManagers]);

  // When a manager is selected (one-at-a-time mode), prefetch their preview.
  useEffect(() => {
    if (managerMode !== 'one' || !selectedManagerId) return;
    loadManagerPreview(selectedManagerId);
  }, [selectedManagerId, managerMode, loadManagerPreview]);

  // ─── Senders ───────────────────────────────────────────────────────────────
  const sendTeamDigest = async () => {
    setTeamSending(true);
    try {
      const resp = await apiCall(`${API_BASE_URL}/api/Admin/team-digest/send`, {
        method: 'POST',
        body: JSON.stringify({
          recipients: teamRecipients,
          subject: teamSubject || undefined
        })
      });
      const json = await resp.json();
      if (resp.ok && json?.data) {
        const r: BulkSendResult = json.data;
        if (r.failed === 0) {
          showSuccess(`Team digest sent to ${r.succeeded} recipient${r.succeeded === 1 ? '' : 's'}.`);
        } else {
          showError(`Sent to ${r.succeeded} of ${r.total}. ${r.failed} failed — check console.`);
          // eslint-disable-next-line no-console
          console.warn('Team digest send failures:', r.results.filter((x) => !x.success));
        }
      } else {
        showError(json?.message || 'Failed to send team digest');
      }
    } catch {
      showError('Failed to send team digest');
    } finally {
      setTeamSending(false);
      setTeamConfirmOpen(false);
    }
  };

  const sendManagerDigestSingle = async () => {
    if (!selectedManagerId) return;
    setManagerSending(true);
    try {
      const resp = await apiCall(`${API_BASE_URL}/api/Admin/manager-digest/send`, {
        method: 'POST',
        body: JSON.stringify({
          managerId: selectedManagerId,
          cc: managerCc,
          subject: managerSubject || undefined
        })
      });
      const json = await resp.json();
      if (resp.ok && json?.data) {
        const r: BulkSendResult = json.data;
        if (r.failed === 0) {
          showSuccess(`Sent to ${r.succeeded} recipient${r.succeeded === 1 ? '' : 's'}.`);
        } else {
          showError(`Sent to ${r.succeeded} of ${r.total}. ${r.failed} failed.`);
        }
      } else {
        showError(json?.message || 'Failed to send manager digest');
      }
    } catch {
      showError('Failed to send manager digest');
    } finally {
      setManagerSending(false);
      setManagerConfirmOpen(false);
    }
  };

  const sendManagerDigestBulk = async () => {
    if (bulkSelectedIds.size === 0) return;
    setBulkSending(true);
    setBulkResult(null);
    try {
      const resp = await apiCall(`${API_BASE_URL}/api/Admin/manager-digest/send-bulk`, {
        method: 'POST',
        body: JSON.stringify({
          managerIds: Array.from(bulkSelectedIds),
          cc: bulkCc,
          subject: bulkSubject || undefined
        })
      });
      const json = await resp.json();
      if (resp.ok && json?.data) {
        const r: BulkSendResult = json.data;
        setBulkResult(r);
        if (r.failed === 0) {
          showSuccess(`Sent personalized digests to ${r.succeeded} manager${r.succeeded === 1 ? '' : 's'}.`);
        } else {
          showError(`${r.succeeded} sent, ${r.failed} failed. See breakdown below.`);
        }
      } else {
        showError(json?.message || 'Failed to send bulk manager digest');
      }
    } catch {
      showError('Failed to send bulk manager digest');
    } finally {
      setBulkSending(false);
      setBulkConfirmOpen(false);
    }
  };

  // ─── Derived ───────────────────────────────────────────────────────────────
  const filteredManagers = useMemo(() => {
    const q = managerSearch.trim().toLowerCase();
    if (!q) return managers;
    return managers.filter(
      (m) =>
        m.name.toLowerCase().includes(q) ||
        m.email.toLowerCase().includes(q) ||
        m.employeeId.toLowerCase().includes(q)
    );
  }, [managers, managerSearch]);

  // ─── Render gates ──────────────────────────────────────────────────────────
  if (authChecking) {
    return (
      <div className="pt-24 min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-pink-500 animate-spin" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="pt-24 min-h-screen bg-slate-50">
        <main className="mx-auto max-w-2xl px-6 py-16">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-10 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-red-50 mb-4">
              <ShieldCheck className="w-8 h-8 text-red-500" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Admin access required</h2>
            <p className="text-slate-600 mb-6">
              This page is restricted to platform admins. If you believe you should have access,
              please ask another admin to add your email to the <code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">Admins:Emails</code> configuration list.
            </p>
            <button
              onClick={() => navigate('/dashboard')}
              className="px-5 py-2.5 rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 transition-colors"
            >
              Back to Dashboard
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="pt-24 min-h-screen bg-slate-50">
      <main className="mx-auto px-6 lg:px-12 xl:px-20 py-8 max-w-[1400px]">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-pink-500 hover:text-pink-600 transition-colors mb-4 font-medium"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Dashboard
          </button>

          <div className="bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-2xl p-8 shadow-xl text-white">
            <div className="flex items-center gap-4 mb-2">
              <Mail className="w-10 h-10" />
              <h1 className="text-3xl font-bold">Connect Digest Admin</h1>
            </div>
            <p className="text-white/85 ml-14">
              Preview, customize recipients, and send the bi-weekly Manager Digest or the monthly Team Digest.
            </p>
          </div>
        </div>

        {/* Top-level tabs */}
        <div className="bg-white rounded-2xl p-2 shadow-sm border border-slate-200 mb-6 flex gap-2">
          <TabButton
            active={activeTab === 'manager'}
            onClick={() => setActiveTab('manager')}
            icon={<UserCog className="w-4 h-4" />}
            title="Manager Digest"
            subtitle="Biweekly — one personalized email per manager"
          />
          <TabButton
            active={activeTab === 'team'}
            onClick={() => {
              setActiveTab('team');
              if (!teamPreviewHtml && !teamPreviewLoading) loadTeamPreview();
            }}
            icon={<Users className="w-4 h-4" />}
            title="Team Digest"
            subtitle="Monthly — one BU-wide email to many recipients"
          />
        </div>

        {/* ─── Team Digest tab ─────────────────────────────────────────────── */}
        {activeTab === 'team' && (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Compose card */}
            <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
              <h2 className="text-lg font-bold text-slate-900 mb-1 flex items-center gap-2">
                <Send className="w-5 h-5 text-pink-500" />
                Compose
              </h2>
              <p className="text-sm text-slate-500 mb-5">
                Add recipients (one at a time, Outlook-style) and send the same BU-wide HTML body to each.
              </p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Subject (optional)</label>
                  <input
                    type="text"
                    value={teamSubject}
                    onChange={(e) => setTeamSubject(e.target.value)}
                    placeholder="MSIDC BU — Connects Status Digest"
                    disabled={teamSending}
                    className="w-full px-3 py-2.5 rounded-xl border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-pink-500/20 focus:border-pink-400 disabled:bg-slate-50"
                  />
                </div>

                <RecipientPicker
                  value={teamRecipients}
                  onChange={setTeamRecipients}
                  label="To"
                  placeholder="Type a name or email and press Enter…"
                  disabled={teamSending}
                />

                <div className="flex items-center justify-between pt-2">
                  <button
                    onClick={loadTeamPreview}
                    disabled={teamPreviewLoading}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-300 bg-white text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors disabled:opacity-50"
                  >
                    {teamPreviewLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4" />
                    )}
                    Refresh preview
                  </button>

                  <button
                    onClick={() => setTeamConfirmOpen(true)}
                    disabled={teamSending || teamRecipients.length === 0}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-pink-500 to-purple-600 text-white font-semibold text-sm shadow-sm hover:from-pink-600 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {teamSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    Send to {teamRecipients.length || 0}
                  </button>
                </div>
              </div>
            </div>

            {/* Preview iframe */}
            <div className="lg:col-span-3 bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden flex flex-col">
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
                <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                  <Eye className="w-5 h-5 text-indigo-500" />
                  Email Preview
                </h2>
                <span className="text-xs text-slate-500">Rendered inside a sandboxed iframe</span>
              </div>
              <PreviewFrame html={teamPreviewHtml} loading={teamPreviewLoading} emptyHint="Click Refresh preview to load." />
            </div>
          </div>
        )}

        {/* ─── Manager Digest tab ─────────────────────────────────────────── */}
        {activeTab === 'manager' && (
          <div className="space-y-6">
            {/* Sub-mode toggle */}
            <div className="bg-white rounded-2xl p-2 shadow-sm border border-slate-200 flex gap-2 max-w-xl">
              <SubTabButton
                active={managerMode === 'one'}
                onClick={() => setManagerMode('one')}
                icon={<Eye className="w-4 h-4" />}
                label="By manager"
              />
              <SubTabButton
                active={managerMode === 'bulk'}
                onClick={() => setManagerMode('bulk')}
                icon={<ListChecks className="w-4 h-4" />}
                label="Bulk send"
              />
            </div>

            {managerMode === 'one' && (
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                {/* Compose card */}
                <div className="lg:col-span-2 space-y-6">
                  <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
                    <h2 className="text-lg font-bold text-slate-900 mb-1 flex items-center gap-2">
                      <UserCog className="w-5 h-5 text-indigo-500" />
                      Pick a manager
                    </h2>
                    <p className="text-sm text-slate-500 mb-4">
                      Pick one manager to preview their personalized digest, then choose recipients.
                    </p>

                    <div className="relative mb-3">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input
                        type="text"
                        value={managerSearch}
                        onChange={(e) => setManagerSearch(e.target.value)}
                        placeholder="Search managers…"
                        className="w-full bg-slate-50 text-slate-800 border border-slate-200 rounded-xl pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-pink-500/20 focus:border-pink-300"
                      />
                    </div>

                    <div className="max-h-64 overflow-y-auto border border-slate-200 rounded-xl divide-y divide-slate-100">
                      {managersLoading ? (
                        <div className="flex items-center justify-center py-8 text-slate-500 text-sm">
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          Loading managers…
                        </div>
                      ) : filteredManagers.length === 0 ? (
                        <div className="px-4 py-6 text-center text-sm text-slate-500">
                          No managers match your search.
                        </div>
                      ) : (
                        filteredManagers.map((m) => (
                          <button
                            key={m.employeeId}
                            type="button"
                            onClick={() => setSelectedManagerId(m.employeeId)}
                            className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-colors
                              ${selectedManagerId === m.employeeId ? 'bg-pink-50' : 'hover:bg-slate-50'}`}
                          >
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                              {m.name.charAt(0).toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-slate-800 truncate text-sm">{m.name}</div>
                              <div className="text-xs text-slate-500 truncate">{m.email || '— no email —'}</div>
                            </div>
                            <span className="text-xs font-semibold text-slate-500 bg-slate-100 rounded-full px-2 py-0.5 flex-shrink-0">
                              {m.directReportCount}
                            </span>
                          </button>
                        ))
                      )}
                    </div>
                  </div>

                  <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
                    <h2 className="text-lg font-bold text-slate-900 mb-1 flex items-center gap-2">
                      <Send className="w-5 h-5 text-pink-500" />
                      Compose
                    </h2>
                    <p className="text-sm text-slate-500 mb-4">
                      Sent to the selected manager's own email — add anyone else to CC below.
                    </p>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Subject (optional)</label>
                        <input
                          type="text"
                          value={managerSubject}
                          onChange={(e) => setManagerSubject(e.target.value)}
                          placeholder="Your Bi-weekly Connects Digest"
                          disabled={managerSending}
                          className="w-full px-3 py-2.5 rounded-xl border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-pink-500/20 focus:border-pink-400 disabled:bg-slate-50"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">To</label>
                        <div className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-sm text-slate-600">
                          {selectedManagerId
                            ? managers.find((m) => m.employeeId === selectedManagerId)?.email || '— no email on file —'
                            : 'Select a manager on the left'}
                        </div>
                      </div>

                      <RecipientPicker
                        value={managerCc}
                        onChange={setManagerCc}
                        label="CC (optional)"
                        placeholder="Type a name or email and press Enter…"
                        disabled={managerSending}
                      />

                      <div className="flex items-center justify-end pt-2">
                        <button
                          onClick={() => setManagerConfirmOpen(true)}
                          disabled={managerSending || !selectedManagerId}
                          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-pink-500 to-purple-600 text-white font-semibold text-sm shadow-sm hover:from-pink-600 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {managerSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                          Send digest
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Preview iframe */}
                <div className="lg:col-span-3 bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden flex flex-col">
                  <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
                    <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                      <Eye className="w-5 h-5 text-indigo-500" />
                      Email Preview
                    </h2>
                    {selectedManagerId && (
                      <button
                        onClick={() => loadManagerPreview(selectedManagerId)}
                        disabled={managerPreviewLoading}
                        className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 transition-colors disabled:opacity-50"
                      >
                        {managerPreviewLoading ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <RefreshCw className="w-3 h-3" />
                        )}
                        Refresh
                      </button>
                    )}
                  </div>
                  <PreviewFrame
                    html={managerPreviewHtml}
                    loading={managerPreviewLoading}
                    emptyHint="Pick a manager on the left to preview their personalized digest."
                  />
                </div>
              </div>
            )}

            {managerMode === 'bulk' && (
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
                <h2 className="text-lg font-bold text-slate-900 mb-1 flex items-center gap-2">
                  <ListChecks className="w-5 h-5 text-pink-500" />
                  Bulk send
                </h2>
                <p className="text-sm text-slate-500 mb-5">
                  Tick the managers below. Each selected manager will receive their own personalized digest at their own email address.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5 items-center">
                  <div className="md:col-span-2 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      value={managerSearch}
                      onChange={(e) => setManagerSearch(e.target.value)}
                      placeholder="Search managers…"
                      className="w-full bg-slate-50 text-slate-800 border border-slate-200 rounded-xl pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-pink-500/20 focus:border-pink-300"
                    />
                  </div>
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => {
                        const next = new Set(bulkSelectedIds);
                        filteredManagers.forEach((m) => next.add(m.employeeId));
                        setBulkSelectedIds(next);
                      }}
                      className="text-xs font-semibold text-pink-600 hover:text-pink-700 transition-colors"
                    >
                      Select all
                    </button>
                    <span className="text-slate-300">|</span>
                    <button
                      onClick={() => setBulkSelectedIds(new Set())}
                      className="text-xs font-semibold text-slate-500 hover:text-slate-700 transition-colors"
                    >
                      Clear
                    </button>
                  </div>
                </div>

                <div className="border border-slate-200 rounded-xl divide-y divide-slate-100 max-h-[420px] overflow-y-auto mb-5">
                  {managersLoading ? (
                    <div className="flex items-center justify-center py-10 text-slate-500 text-sm">
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Loading managers…
                    </div>
                  ) : filteredManagers.length === 0 ? (
                    <div className="px-4 py-6 text-center text-sm text-slate-500">No managers match.</div>
                  ) : (
                    filteredManagers.map((m) => {
                      const checked = bulkSelectedIds.has(m.employeeId);
                      const hasEmail = Boolean(m.email);
                      return (
                        <label
                          key={m.employeeId}
                          className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors
                            ${checked ? 'bg-pink-50' : 'hover:bg-slate-50'}
                            ${!hasEmail ? 'opacity-60' : ''}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={!hasEmail || bulkSending}
                            onChange={() => {
                              const next = new Set(bulkSelectedIds);
                              if (next.has(m.employeeId)) next.delete(m.employeeId);
                              else next.add(m.employeeId);
                              setBulkSelectedIds(next);
                            }}
                            className="w-4 h-4 accent-pink-500"
                          />
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                            {m.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-slate-800 truncate text-sm">{m.name}</div>
                            <div className="text-xs text-slate-500 truncate">
                              {m.email || <span className="italic text-amber-600">no email on file</span>}
                            </div>
                          </div>
                          <span className="text-xs font-semibold text-slate-500 bg-slate-100 rounded-full px-2 py-0.5 flex-shrink-0">
                            {m.directReportCount} reports
                          </span>
                        </label>
                      );
                    })
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end mb-5">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Subject (optional)</label>
                    <input
                      type="text"
                      value={bulkSubject}
                      onChange={(e) => setBulkSubject(e.target.value)}
                      placeholder="Your Bi-weekly Connects Digest"
                      disabled={bulkSending}
                      className="w-full px-3 py-2.5 rounded-xl border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-pink-500/20 focus:border-pink-400 disabled:bg-slate-50"
                    />
                  </div>
                </div>

                <div className="mb-5">
                  <RecipientPicker
                    value={bulkCc}
                    onChange={setBulkCc}
                    label="CC (optional, applied to every manager's email)"
                    placeholder="Type a name or email and press Enter…"
                    disabled={bulkSending}
                  />
                </div>

                <div className="flex items-center justify-end mb-5">
                  <button
                    onClick={() => setBulkConfirmOpen(true)}
                    disabled={bulkSending || bulkSelectedIds.size === 0}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-pink-500 to-purple-600 text-white font-semibold text-sm shadow-sm hover:from-pink-600 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {bulkSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    Send to {bulkSelectedIds.size} manager{bulkSelectedIds.size === 1 ? '' : 's'}
                  </button>
                </div>

                {bulkResult && (
                  <div className="mt-6 border border-slate-200 rounded-xl overflow-hidden">
                    <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                      <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                        <CalendarDays className="w-4 h-4" />
                        Send results
                      </h3>
                      <span className="text-xs font-semibold text-slate-600">
                        {bulkResult.succeeded} ok · {bulkResult.failed} failed · {bulkResult.total} total
                      </span>
                    </div>
                    <div className="divide-y divide-slate-100 max-h-60 overflow-y-auto">
                      {bulkResult.results.map((r, idx) => (
                        <div key={idx} className="flex items-center gap-3 px-4 py-2 text-sm">
                          {r.success ? (
                            <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                          ) : (
                            <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                          )}
                          <span className="text-slate-700 truncate flex-1">{r.email}</span>
                          {!r.success && r.error && (
                            <span className="text-xs text-red-600 truncate max-w-[40%]">{r.error}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </main>

      {/* ─── Confirm dialogs ─────────────────────────────────────────────── */}
      {teamConfirmOpen && (
        <ConfirmDialog
          title="Send team digest?"
          message={`You are about to send the BU-wide team digest to ${teamRecipients.length} recipient${teamRecipients.length === 1 ? '' : 's'}. This action cannot be undone.`}
          confirmText={`Send to ${teamRecipients.length}`}
          cancelText="Cancel"
          onConfirm={sendTeamDigest}
          onCancel={() => setTeamConfirmOpen(false)}
          type="info"
        />
      )}

      {managerConfirmOpen && (
        <ConfirmDialog
          title="Send manager digest?"
          message={`You are about to send this manager's personalized digest to their own email${managerCc.length > 0 ? `, CC'ing ${managerCc.length} other${managerCc.length === 1 ? '' : 's'}` : ''}.`}
          confirmText="Send"
          cancelText="Cancel"
          onConfirm={sendManagerDigestSingle}
          onCancel={() => setManagerConfirmOpen(false)}
          type="info"
        />
      )}

      {bulkConfirmOpen && (
        <ConfirmDialog
          title="Bulk send manager digests?"
          message={`Each of the ${bulkSelectedIds.size} selected manager${bulkSelectedIds.size === 1 ? '' : 's'} will receive their own personalized digest at their own email address${bulkCc.length > 0 ? `, CC'ing ${bulkCc.length} other${bulkCc.length === 1 ? '' : 's'} on every send` : ''}.`}
          confirmText={`Send ${bulkSelectedIds.size} email${bulkSelectedIds.size === 1 ? '' : 's'}`}
          cancelText="Cancel"
          onConfirm={sendManagerDigestBulk}
          onCancel={() => setBulkConfirmOpen(false)}
          type="warning"
        />
      )}

      {/* ─── Toasts ──────────────────────────────────────────────────────── */}
      {toasts.map((t) => (
        <Toast key={t.id} message={t.message} type={t.type} onClose={() => hideToast(t.id)} />
      ))}
    </div>
  );
}

// ─── Small inner components ─────────────────────────────────────────────────

interface TabButtonProps {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  title: string;
  subtitle: string;
}

const TabButton: React.FC<TabButtonProps> = ({ active, onClick, icon, title, subtitle }) => (
  <button
    onClick={onClick}
    className={`flex-1 text-left px-5 py-3 rounded-xl transition-all
      ${active
        ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-md'
        : 'text-slate-700 hover:bg-slate-50'}`}
  >
    <div className={`flex items-center gap-2 font-bold text-sm ${active ? 'text-white' : 'text-slate-800'}`}>
      {icon}
      {title}
    </div>
    <div className={`text-xs mt-0.5 ${active ? 'text-white/80' : 'text-slate-500'}`}>{subtitle}</div>
  </button>
);

interface SubTabButtonProps {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}

const SubTabButton: React.FC<SubTabButtonProps> = ({ active, onClick, icon, label }) => (
  <button
    onClick={onClick}
    className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all
      ${active
        ? 'bg-slate-900 text-white shadow-sm'
        : 'text-slate-600 hover:bg-slate-50'}`}
  >
    {icon}
    {label}
  </button>
);

interface PreviewFrameProps {
  html: string;
  loading: boolean;
  emptyHint: string;
}

const PreviewFrame: React.FC<PreviewFrameProps> = ({ html, loading, emptyHint }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-[600px] text-slate-500">
        <Loader2 className="w-6 h-6 animate-spin mr-2" />
        Generating preview…
      </div>
    );
  }
  if (!html) {
    return (
      <div className="flex items-center justify-center h-[600px] text-slate-400 text-sm italic px-8 text-center">
        {emptyHint}
      </div>
    );
  }
  return (
    <iframe
      title="Digest preview"
      srcDoc={html}
      sandbox="allow-same-origin"
      className="w-full h-[600px] border-0"
    />
  );
};
